/**
 * RefineryActions: what staff can do from the Data refinery with what is
 * ticked. Generate tasks asks Task generation to plan the ticked tests
 * again, optionally at a card count of its own, and starts a run when the
 * job can; when it cannot, the request waits for the next run. Exclude and
 * Include keep the ticked tasks out of every dataset build or let the
 * builds train on them again.
 *
 * Staff only, on a team they are a member of (useStaffTeam says who). The
 * api checks all of it again. Generate tasks works on the scored
 * benchmark only, which is the one generation plans tests from. The bar
 * shows once something is ticked: the count, Generate tasks while tests
 * are ticked, Exclude and Include while tasks are, and Clear. The line
 * under it says where the last Generate ended, and stays once the
 * selection clears.
 *
 * Each button owns its mutation and its dialog, so an answer lands while
 * the dialog is open (ConfirmDialog). A dialog asks about what was ticked
 * when it opened and sends that, and the selection clears once an action
 * lands.
 */

import { useId, useState } from 'react'

import type { RefineryTaskKey, RefineryTreeResponse } from '@/api/generated/model'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TASK_GENERATION_JOB, useGenerateTasks, useSetExcluded } from '@/hooks/useRefineryActions'
import { jobBlocker, useTeamJobs } from '@/hooks/useTeamJobs'
import { cn } from '@/lib/utils'

import {
  type GenerateResult,
  REFINERY_COPY,
  askedTestLabel,
  blockedLabel,
  excludeTitle,
  generateResultLabel,
  generateTitle,
  includeTitle,
  selectedLabel,
} from './rows'
import { type Ticked, type TickedTest, parseCards, selectedTasks, selectedTests } from './tree'
import { useStaffTeam } from './useStaffTeam'

const COPY = REFINERY_COPY.actions

interface RefineryActionsProps {
  tree: RefineryTreeResponse
  ticked: Ticked
  /** Clears the selection: Clear, and every action once it lands. */
  onDone: () => void
}

export function RefineryActions(props: RefineryActionsProps): React.JSX.Element | null {
  const team = useStaffTeam()
  if (team === null) return null
  return <ActionBar {...props} teamId={team.id} />
}

/** The last Generate, for the line under the bar. */
interface Landed {
  result: GenerateResult
  tests: number
}

function ActionBar({ tree, ticked, onDone, teamId }: RefineryActionsProps & { teamId: string }): React.JSX.Element | null {
  const [landed, setLanded] = useState<Landed | null>(null)
  const anyTicked = ticked.tests.size > 0 || ticked.tasks.size > 0
  if (!anyTicked && landed === null) return null
  return (
    <div className="space-y-2">
      {anyTicked && (
        <div role="group" aria-label={COPY.label} className="flex flex-wrap items-center gap-2">
          <TickedActions
            tree={tree}
            ticked={ticked}
            teamId={teamId}
            onDone={onDone}
            onGenerated={(next) => {
              setLanded(next)
              onDone()
            }}
            onSaved={() => {
              setLanded(null)
              onDone()
            }}
          />
        </div>
      )}
      <p
        role="status"
        className={cn('text-sm empty:hidden', landed?.result.kind === 'refused' ? 'text-destructive' : 'text-muted-foreground')}
      >
        {landed === null ? '' : generateResultLabel(landed.result, landed.tests)}
      </p>
    </div>
  )
}

interface TickedActionsProps {
  tree: RefineryTreeResponse
  ticked: Ticked
  teamId: string
  onDone: () => void
  onGenerated: (landed: Landed) => void
  /** An exclusions save landed. */
  onSaved: () => void
}

/** The count, then each action the ticked tests and tasks allow, then Clear. */
function TickedActions({ tree, ticked, teamId, onDone, onGenerated, onSaved }: TickedActionsProps): React.JSX.Element {
  const tests = tree.scored ? selectedTests(tree.tests, ticked.tests) : []
  const tasks = selectedTasks(tree.tests, tree.unresolved, ticked.tasks)
  const revision = tree.exclusions_revision
  return (
    <>
      <span className="font-mono text-xs tabular-nums text-muted-foreground">
        {selectedLabel(ticked.tests.size, ticked.tasks.size)}
      </span>
      {tests.length > 0 && (
        <GenerateButton benchmark={tree.benchmark} tests={tests} teamId={teamId} onLanded={onGenerated} />
      )}
      {ticked.tasks.size > 0 && (
        <>
          <ExclusionButton excluded tasks={tasks.excludable} revision={revision} onDone={onSaved} />
          <ExclusionButton excluded={false} tasks={tasks.includable} revision={revision} onDone={onSaved} />
        </>
      )}
      <Button variant="ghost" size="sm" onClick={onDone}>
        {COPY.clear}
      </Button>
    </>
  )
}

/**
 * Whether Generate may start a run once the request is saved: not known
 * while the jobs load, else ready, or blocked with the job row's reason
 * (a run of the team's that is going reads `actions.running`).
 */
type StartGate = { kind: 'loading' } | { kind: 'ready' } | { kind: 'blocked'; reason: string }

function useStartGate(teamId: string): StartGate {
  const { jobs, error } = useTeamJobs(teamId)
  const job = jobs.find((listed) => listed.slug === TASK_GENERATION_JOB)
  if (error !== null) return { kind: 'blocked', reason: error }
  if (job === undefined) return { kind: 'loading' }
  const reason = jobBlocker(job, COPY.running)
  return reason === null ? { kind: 'ready' } : { kind: 'blocked', reason }
}

/**
 * Generate tasks: one dialog, one answer. Generate saves the request and
 * then starts Task generation, as the staff Jobs page's row does, when the
 * job can start. When it cannot, Generate saves the request only and the
 * dialog says why above the button. It waits while the jobs load, since
 * which of the two it does is not known yet.
 */
function GenerateButton({
  benchmark,
  tests,
  teamId,
  onLanded,
}: {
  benchmark: string
  tests: TickedTest[]
  teamId: string
  onLanded: (landed: Landed) => void
}): React.JSX.Element {
  const generate = useGenerateTasks(teamId)
  const gate = useStartGate(teamId)
  const [open, setOpen] = useState(false)
  // What the dialog asks about, taken again each time it opens.
  const [asked, setAsked] = useState<TickedTest[]>(tests)
  const [cardsText, setCardsText] = useState('')
  const parsed = parseCards(cardsText)
  const confirm = (): void => {
    if (parsed.kind === 'error' || gate.kind === 'loading' || generate.isPending) return
    const data = { source_modules: asked.map((test) => test.module), cards: parsed.cards }
    generate.mutate(
      { data, params: { benchmark }, start: gate.kind === 'ready' },
      {
        onSuccess: (result) => {
          setOpen(false)
          onLanded({ result, tests: asked.length })
        },
      },
    )
  }
  return (
    <>
      <Button
        size="sm"
        onClick={() => {
          setAsked(tests)
          setCardsText('')
          setOpen(true)
        }}
      >
        {COPY.generate}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={generateTitle(asked.length)}
        confirmLabel={COPY.confirmGenerate}
        pendingLabel={gate.kind === 'ready' ? COPY.starting : COPY.saving}
        isPending={generate.isPending}
        confirmDisabled={parsed.kind === 'error' || gate.kind === 'loading'}
        onConfirm={confirm}
      >
        <ul className="space-y-1 text-sm">
          {asked.map((test) => (
            <li key={test.module}>{askedTestLabel(test, asked)}</li>
          ))}
        </ul>
        <CardsField
          value={cardsText}
          onChange={setCardsText}
          error={parsed.kind === 'error' ? parsed.error : undefined}
          onEnter={confirm}
        />
        {gate.kind === 'blocked' && <p className="text-sm text-muted-foreground">{blockedLabel(gate.reason)}</p>}
      </ConfirmDialog>
    </>
  )
}

/** Cards per test on one line: empty plans each test at its tier's count. Enter confirms. */
function CardsField({
  value,
  onChange,
  error,
  onEnter,
}: {
  value: string
  onChange: (next: string) => void
  error: string | undefined
  onEnter: () => void
}): React.JSX.Element {
  const id = useId()
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <Label htmlFor={id}>{COPY.cardsLabel}</Label>
        <Input
          id={id}
          className="w-24"
          inputMode="numeric"
          placeholder={COPY.cardsPlaceholder}
          value={value}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : `${id}-error`}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onEnter()
          }}
        />
      </div>
      <FieldError id={`${id}-error`} message={error} />
    </div>
  )
}

/**
 * Exclude (or Include) the ticked tasks that a save would change, naming
 * the tree's revision. Off while no ticked task would change.
 */
function ExclusionButton({
  excluded,
  tasks,
  revision,
  onDone,
}: {
  excluded: boolean
  tasks: RefineryTaskKey[]
  revision: string | null
  onDone: () => void
}): React.JSX.Element {
  const save = useSetExcluded()
  const [open, setOpen] = useState(false)
  const [asked, setAsked] = useState<RefineryTaskKey[]>([])
  const label = excluded ? COPY.exclude : COPY.include
  const ask = (): void => {
    setAsked(tasks)
    setOpen(true)
  }
  return (
    <>
      <Button variant="outline" size="sm" disabled={tasks.length === 0} onClick={ask}>
        {label}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={excluded ? excludeTitle(asked.length) : includeTitle(asked.length)}
        description={excluded ? COPY.excludeDescription : COPY.includeDescription}
        confirmLabel={label}
        pendingLabel={COPY.saving}
        isPending={save.isPending}
        onConfirm={() => {
          // The revision is the tree's as it reads now: after a refused save
          // the tree is read again, so a second try names the fresh one.
          const data = { tasks: asked, excluded, expected_revision: revision }
          save.mutate(
            { data },
            {
              onSuccess: () => {
                setOpen(false)
                onDone()
              },
            },
          )
        }}
      />
    </>
  )
}
